import {
  Paper,
  Tooltip,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Typography,
  Container,
  Grid,
} from '@material-ui/core';
import { FC, useState, useMemo, useCallback } from 'react';
import { useStyles } from './styles';
import { ConfirmActionDialog } from 'components/Dialogs';
import { GridColumns, DataGrid, GridCellParams } from '@material-ui/data-grid';
import PreviewIcon from '@material-ui/icons/RemoveRedEyeSharp';
import { orange } from '@material-ui/core/colors';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import { useStore } from 'hooks/useRootStore';
import { useMutation, useQuery } from 'react-query';
import { useAppPreloader } from 'hooks/useAppPreloader';
import { TableData } from './types';
import { getRowsPerPageOptions } from 'utils/pagination';
import { toFormatDateTime } from 'utils';
import { DepartmentAction } from 'components/Dialogs/DepartmentAction';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';

const CommonTable: FC<TableData> = ({
  fieldtable,
  getData,
  deleteData,
  deleteLabel,
  view,
  clickedData,
  tableIcon,
  viewTable,
  dataHeadName,
  dataName,
  editTable,
  dataValue,
  user,
  syncData,
  loading,
  clickedItem,
  dataLabel,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const {
    dashboardStore: { getldapStatistic },
    notificationStore: { openNotification },
  } = useStore();
  const [showMessage, setShowMessage] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState<boolean>(false);
  const [openDepartmentDialog, setOpenDepartmentDialog] = useState<boolean>(false);
  const [appStatus, setAppStatus] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const getDataReq = useQuery('getDataReq', () => getData(), {
    onError: () => openNotification('error', `Can't get ${fieldtable}s`),
  });
  const profileFromStorage = JSON.parse(localStorage.getItem('Profile') || 'null');
  const deleteDataReq = useMutation(
    ({ status, appStatus }: { status: string; appStatus: boolean }) => deleteData(status, appStatus),
    {
      mutationKey: 'deleteDataReq',
      onSuccess: () => {
        openNotification('success', `Deleted successfully`);
        getDataReq.refetch();
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Group cannot be deleted.';
        openNotification('error', errorMessage);
      },
    }
  );

  const rows = useMemo(() => {
    const dataToUse = view && view.length > 0 ? view : getDataReq.data;
    if (dataToUse && Array.isArray(dataToUse)) {
      return dataToUse.map((item, index) => {
        // Create a safe ID by encoding special characters
        const safeId = item.dn
          ? btoa(item.dn).replace(/[^a-zA-Z0-9]/g, '_')
          : item.ou
            ? btoa(item.ou).replace(/[^a-zA-Z0-9]/g, '_')
            : `row_${index}`;
        return {
          ...item,
          id: safeId,
          originalId: item.dn || item.ou || index, // Keep original for reference
        };
      });
    }
    return [];
  }, [view, getDataReq.data]);

  const getldapStatisticReq = useQuery(
    'getldapStatisticReq',
    () => getldapStatistic(dataName === 'ou' ? 'departments' : 'groups'),
    {
      onError: () => openNotification('error', `Can't get statistic`),
      enabled: profileFromStorage?.permission !== 'EndUser' && profileFromStorage?.permission !== 'Manager',
    }
  );
  const appStatistics = getldapStatisticReq?.data?.data;

  const [pageSize, setPageSize] = useState(10);
  const rowsPerPageOptions = useMemo(() => getRowsPerPageOptions(rows), [rows]);
  const formattedRows = rows.map((row: { modifytimestamp: string | null; createtimestamp: string | null }) => {
    return {
      ...row,
      createtimestamp: toFormatDateTime(row.createtimestamp),
      modifytimestamp: toFormatDateTime(row.modifytimestamp),
    };
  });

  // const handleDeleteAction = useCallback(() => {
  //   deleteDataReq.mutate({ status: dataValue, appStatus });
  // }, [dataValue, appStatus, deleteDataReq]);

  const handleDeleteAction = useCallback(() => {
    deleteDataReq.mutate(
      {
        status: dataValue,
        appStatus,
      },
      {
        onSuccess: () => {
          getldapStatisticReq.refetch();
        },
      }
    );
  }, [dataValue, appStatus, deleteDataReq, getldapStatisticReq]);

  const hasManagerData = rows.some((row) => row.managersData && row.managersData.length > 0);
  const columns: GridColumns = [
    // Icon column - always visible
    {
      field: 'icon',
      headerName: ' ',
      align: 'center',
      width: isMobile ? 40 : 50,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params: GridCellParams) => tableIcon,
    },
    // {
    //   field: 'createtimestamp',
    //   headerName: (user === 'Manager' || user === 'EndUser' ? 'ASSIGNED' : 'CREATED'),
    //   width: isMobile ? 200 : isTablet ? 120 : 150,
    //   flex: isMobile ? 1.5 : isTablet ? 0.6 : 0.8,
    //   renderHeader: () => {
    //     const headerText = user === 'Manager' || user === 'EndUser' ? 'ASSIGNED' : 'CREATED';
    //     return (
    //       <Tooltip
    //         title={headerText}
    //         placement='top'
    //         enterTouchDelay={0}
    //         leaveTouchDelay={1500}
    //         arrow
    //       >
    //         <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer' }}>
    //           {isMobile && headerText.length > 8 ? headerText.substring(0, 8) + '...' : headerText}
    //         </span>
    //       </Tooltip>
    //     );
    //   },
    //   renderCell: (params: GridCellParams) => {
    //     const value = String(params.value ?? '');
    //     return (
    //       <Tooltip title={value || ''} placement='bottom'>
    //         <div style={{
    //           overflow: 'hidden',
    //           textOverflow: 'ellipsis',
    //           whiteSpace: 'nowrap',
    //           maxWidth: '100%',
    //           fontSize: isMobile ? '11px' : '13px'
    //         }}>
    //           <span>{value || '-'}</span>
    //         </div>
    //       </Tooltip>
    //     )
    //   },
    // },
    // {
    //   field: 'modifytimestamp',
    //   headerName: (user === 'Manager' || user === 'EndUser' ? 'REMOVED' : 'UPDATED'),
    //   width: isMobile ? 200 : isTablet ? 120 : 150,
    //   flex: isMobile ? 1.5 : isTablet ? 0.6 : 0.8,
    //   renderHeader: () => {
    //     const headerText = user === 'Manager' || user === 'EndUser' ? 'REMOVED' : 'UPDATED';
    //     return (
    //       <Tooltip
    //         title={headerText}
    //         placement='top'
    //         enterTouchDelay={0}
    //         leaveTouchDelay={1500}
    //         arrow
    //       >
    //         <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer' }}>
    //           {isMobile && headerText.length > 8 ? headerText.substring(0, 8) + '...' : headerText}
    //         </span>
    //       </Tooltip>
    //     );
    //   },
    //   renderCell: (params: GridCellParams) => {
    //     const value = String(params.value ?? '');
    //     return (
    //       <Tooltip title={value || ''} placement='bottom'>
    //         <div style={{
    //           overflow: 'hidden',
    //           textOverflow: 'ellipsis',
    //           whiteSpace: 'nowrap',
    //           maxWidth: '100%',
    //           fontSize: isMobile ? '11px' : '13px'
    //         }}>
    //           <span>{value || '-'}</span>
    //         </div>
    //       </Tooltip>
    //     )
    //   },
    // },
    {
      field: dataName,
      headerName: dataHeadName ? dataHeadName : 'DETAIL',
      width: isMobile ? 250 : isTablet ? 160 : 120,
      flex: isMobile ? 2 : isTablet ? 0.8 : 0.9,
      renderHeader: () => {
        const headerText = dataHeadName ? dataHeadName : 'DETAIL';
        return (
          <Tooltip title={headerText} placement='top' enterTouchDelay={0} leaveTouchDelay={1500} arrow>
            <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer' }}>
              {headerText}
            </span>
          </Tooltip>
        );
      },
      renderCell: (params: GridCellParams) => {
        const value = String(params.value ?? '');
        return (
          <Tooltip title={value || ''} placement='bottom'>
            <div
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '100%',
                fontSize: isMobile ? '13px' : '14px',
                fontWeight: isMobile ? '500' : 'normal',
              }}
            >
              <span>{value}</span>
            </div>
          </Tooltip>
        );
      },
    },
    ...(user !== 'EndUser' && user !== 'Manager'
      ? [
        {
          field: 'description',
          headerName: 'DESCRIPTION',
          width: isMobile ? 200 : isTablet ? 150 : 200,
          flex: isMobile ? 3 : isTablet ? 0.8 : 1,
          renderHeader: () => {
            const headerText = 'DESCRIPTION';
            return (
              <Tooltip title='DESCRIPTION' placement='top' enterTouchDelay={0} leaveTouchDelay={1500} arrow>
                <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer' }}>
                  {headerText}
                </span>
              </Tooltip>
            );
          },
          renderCell: (params: GridCellParams) => {
            const value = String(params.value ?? '');
            return (
              <Tooltip title={value || ''} placement='bottom'>
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    fontSize: isMobile ? '11px' : isTablet ? '12px' : '14px',
                  }}
                >
                  <span>{value || '-'}</span>
                </div>
              </Tooltip>
            );
          },
        },
      ]
      : []),
    ...(user !== 'EndUser' && user !== 'Manager'
      ? [
        {
          field: 'googleId',
          headerName: 'SYNC',
          width: 100,
          sortable: false,
          renderHeader: () => (
            <Tooltip title='SYNC STATUS' placement='top' enterTouchDelay={0} leaveTouchDelay={1500} arrow>
              <span style={{ fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}>SYNC</span>
            </Tooltip>
          ),
          renderCell: (params: GridCellParams) => {
            const { google_id, zoom_id, microsoft_id, orgId, githubOrgId } = params.row;
            const isOU = dataName === 'ou';
            const thirdConnected = google_id && zoom_id && microsoft_id;
            const bothConnected = (google_id && zoom_id) || (google_id && microsoft_id) || (zoom_id && microsoft_id);
            const onlyOneConnected = google_id || zoom_id || microsoft_id;
            let tooltipText = 'Not Connected';
            if (isOU) {
              if (orgId && githubOrgId) {
                tooltipText = 'Google Workspace, GitHub Workspace';
              } else if (orgId) {
                tooltipText = 'Google Workspace';
              } else if (githubOrgId) {
                tooltipText = 'GitHub Workspace';
              }
            } else if (thirdConnected) {
              tooltipText = 'Google Workspace, Zoom Workspace, Microsoft Workspace';
            } else if (bothConnected) {
              if (google_id && zoom_id) tooltipText = 'Google Workspace, Zoom Workspace';
              else if (google_id && microsoft_id) tooltipText = 'Google Workspace, Microsoft Workspace';
              else if (zoom_id && microsoft_id) tooltipText = 'Zoom Workspace, Microsoft Workspace';
            } else if (onlyOneConnected) {
              if (google_id) tooltipText = 'Google Workspace';
              else if (zoom_id) tooltipText = 'Zoom Workspace';
              else if (microsoft_id) tooltipText = 'Microsoft Workspace';
            }

            // const iconSize = isMobile ? 18 : isTablet ? 22 : 26;
            // const checkIconSize = isMobile ? 12 : isTablet ? 14 : 16;

            return (
              <Tooltip title={tooltipText} placement='bottom'>
                <div style={{ display: 'flex', position: 'relative', alignItems: 'center' }}>
                  {isOU ? (
                    orgId && githubOrgId ? (
                      <>
                        <CircularProgress
                          variant='determinate'
                          value={100}
                          size={40}
                          style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                        />
                        <CheckCircleIcon style={{ color: 'green', fontSize: 24, zIndex: 2, margin: 8 }} />
                      </>
                    ) : orgId || githubOrgId ? (
                      <>
                        <CircularProgress
                          variant='determinate'
                          value={100}
                          size={40}
                          style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                        />
                        <CircularProgress
                          variant='determinate'
                          value={66.6}
                          size={40}
                          style={{ color: 'grey', position: 'absolute', zIndex: 2 }}
                        />
                        <CheckCircleIcon style={{ color: 'green', fontSize: 24, zIndex: 3, margin: 8 }} />
                      </>
                    ) : (
                      <>
                        <CircularProgress
                          variant='determinate'
                          value={100}
                          size={40}
                          style={{ color: 'grey', position: 'absolute', zIndex: 1 }}
                        />
                        <CloseIcon style={{ color: 'red', fontSize: 24, zIndex: 2, margin: 8 }} />
                      </>
                    )
                  ) : onlyOneConnected ? (
                    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                      {thirdConnected ? (
                        <>
                          <CircularProgress
                            variant='determinate'
                            value={100}
                            size={40}
                            style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                          />
                          <CheckCircleIcon style={{ color: 'green', fontSize: 24, zIndex: 2, margin: 8 }} />
                        </>
                      ) : bothConnected ? (
                        <>
                          <CircularProgress
                            variant='determinate'
                            value={100}
                            size={40}
                            style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                          />
                          <CircularProgress
                            variant='determinate'
                            value={33.3}
                            size={40}
                            style={{ color: 'grey', position: 'absolute', zIndex: 2 }}
                          />
                          <CheckCircleIcon style={{ color: 'green', fontSize: 24, zIndex: 1, margin: 8 }} />
                        </>
                      ) : (
                        <>
                          <CircularProgress
                            variant='determinate'
                            value={100}
                            size={40}
                            style={{ color: 'green', position: 'absolute', zIndex: 1 }}
                          />
                          <CircularProgress
                            variant='determinate'
                            value={66.6}
                            size={40}
                            style={{ color: 'grey', position: 'absolute', zIndex: 2 }}
                          />
                          <CheckCircleIcon style={{ color: 'green', fontSize: 24, zIndex: 3, margin: 8 }} />
                        </>
                      )}
                    </div>
                  ) : (
                    <>
                      <CircularProgress
                        variant='determinate'
                        value={100}
                        size={40}
                        style={{ color: 'grey', position: 'absolute', zIndex: 1 }}
                      />
                      <CloseIcon style={{ color: 'red', fontSize: 24, zIndex: 2, margin: 8 }} />
                    </>
                  )}
                </div>
              </Tooltip>
            );
          },
        },
      ]
      : []),
    ...(user === 'Manager' && hasManagerData
      ? [
        {
          field: 'managersData',
          headerName: 'Manager',
          width: isMobile ? 200 : isTablet ? 150 : 200,
          flex: isMobile ? 3 : isTablet ? 0.8 : 0.9,
          renderHeader: () => {
            const headerText = 'MANAGER';
            return (
              <Tooltip title='MANAGER' placement='top' enterTouchDelay={0} leaveTouchDelay={1500} arrow>
                <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer' }}>
                  {headerText}
                </span>
              </Tooltip>
            );
          },
          renderCell: (params: GridCellParams) => {
            const managers = params.row.managersData || [];

            const managerNames = managers
              .map((manager: any) => `${manager.firstName} ${manager.secondName}`)
              .join(', ');

            return (
              <Tooltip title={managerNames || ''} placement='bottom'>
                <div
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                    fontSize: isMobile ? '11px' : isTablet ? '12px' : '14px',
                  }}
                >
                  <span>{managerNames || '-'}</span>
                </div>
              </Tooltip>
            );
          },
        },
      ]
      : []),
    ...(user === 'Manager' &&
      ((dataName === 'ou' && profileFromStorage?.managed?.includes('departments')) ||
        (dataName !== 'ou' && profileFromStorage?.managed?.includes('groups')))
      ? [
        {
          field: 'options',
          headerName: 'ACTIONS',
          width: isMobile ? 60 : isTablet ? 80 : 120,
          flex: 0,
          disableColumnMenu: true,
          sortable: false,
          renderHeader: () => (
            <Tooltip title='ACTIONS' placement='top' enterTouchDelay={0} leaveTouchDelay={1500} arrow>
              <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer' }}>
                ACTIONS
              </span>
            </Tooltip>
          ),
          renderCell: (params: GridCellParams) => (
            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
              <Tooltip title={dataName === 'ou' ? 'View Department' : 'View Group'}>
                <IconButton size='small' onClick={viewTable}>
                  <PreviewIcon style={{ color: orange[500], fontSize: isMobile ? 20 : 24 }} />
                </IconButton>
              </Tooltip>
            </div>
          ),
        },
      ]
      : user !== 'EndUser' && user !== 'Manager'
        ? [
          {
            field: 'options',
            headerName: 'ACTIONS',
            width: isMobile ? 70 : isTablet ? 100 : 160,
            flex: 0,
            disableColumnMenu: true,
            sortable: false,
            renderHeader: () => (
              <Tooltip title='ACTIONS' placement='top' enterTouchDelay={0} leaveTouchDelay={1500} arrow>
                <span style={{ fontSize: isMobile ? '12px' : '14px', fontWeight: 'bold', cursor: 'pointer' }}>
                  ACTIONS
                </span>
              </Tooltip>
            ),
            renderCell: (params: GridCellParams) => (
              <div style={{ display: 'flex', gap: isMobile ? '2px' : '4px', justifyContent: 'center', width: '100%' }}>
                <Tooltip title={dataName === 'ou' ? 'View Department' : 'View Group'}>
                  <IconButton size='small' onClick={viewTable}>
                    <PreviewIcon style={{ color: orange[500], fontSize: isMobile ? 18 : 24 }} />
                  </IconButton>
                </Tooltip>
                {!isMobile && (
                  <>
                    <Tooltip title={dataName === 'ou' ? 'Edit Department' : 'Edit Group'}>
                      <IconButton size='small' onClick={editTable}>
                        <EditIcon style={{ fill: '#FF9800', fontSize: 24 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={dataName === 'ou' ? 'Delete Department' : 'Delete Group'}>
                      <IconButton
                        size='small'
                        onClick={() => {
                          setSelectedItem(params.row);
                          if (dataName === 'ou') {
                            setOpenDepartmentDialog(true);
                          } else {
                            setOpenConfirmDialog(true);
                          }
                        }}
                      >
                        <DeleteIcon style={{ fill: '#F44336', fontSize: 24 }} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </div>
            ),
          },
        ]
        : []),
  ];

  useAppPreloader(getDataReq.isFetching || deleteDataReq.isLoading || getldapStatisticReq.isFetching || loading);

  return (
    <>

      {showMessage &&
        dataName !== 'ou' &&
        profileFromStorage?.permission !== 'EndUser' &&
        profileFromStorage?.permission !== 'Manager' && (
          <Paper
            elevation={0}
            style={{
              position: 'relative',
              padding: '16px 40px 16px 20px',
              marginBottom: '16px',
              background: 'linear-gradient(135deg, #fff8f0 0%, #fff3e0 100%)',
              border: '1px solid #ffe0b2',
              borderRadius: 8,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setShowMessage(false)}
              aria-label="Close message"
              style={{
                position: 'absolute',
                top: 5,
                right: 4,
                color: '#bf360c',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>

            <Typography
              variant="subtitle1"
              style={{
                fontWeight: 700,
                marginBottom: 8,
                color: '#bf360c',
              }}
            >
              Since Group and Role are the same, you can simply use Group as Role.
            </Typography>
          </Paper>
        )}

      {profileFromStorage?.permission !== 'EndUser' && profileFromStorage?.permission !== 'Manager' && (
        <>
          <Container maxWidth='xl' className={classes.container}>
            <Grid className={classes.infoContainer} container alignItems='stretch' spacing={3}>
              <Grid item xs={6} sm={4} md={2}>
                <Paper className={classes.gridPaper}>
                  <p className={classes.value}>
                    {dataName === 'ou'
                      ? appStatistics?.new_departments_created_this_week ?? 0
                      : appStatistics?.new_groups_created_this_week ?? 0}
                  </p>
                  <p className={classes.title}>
                    {' '}
                    {dataName === 'ou' ? (
                      <>
                        New Departments
                        <br />
                        This Week
                      </>
                    ) : (
                      <>
                        New Groups
                        <br />
                        This Week
                      </>
                    )}
                  </p>
                </Paper>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <Paper className={classes.gridPaper}>
                  <p className={classes.value}>
                    {dataName === 'ou'
                      ? appStatistics?.new_departments_created_this_month ?? 0
                      : appStatistics?.new_groups_created_this_month ?? 0}
                  </p>
                  <p className={classes.title}>
                    {' '}
                    {dataName === 'ou' ? (
                      <>
                        New Departments
                        <br />
                        This Month
                      </>
                    ) : (
                      <>
                        New Groups
                        <br />
                        This Month
                      </>
                    )}
                  </p>
                </Paper>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <Paper className={classes.gridPaper}>
                  <p className={classes.value}>
                    {dataName === 'ou'
                      ? appStatistics?.departments_synced_this_week ?? 0
                      : appStatistics?.groups_synced_this_week ?? 0}
                  </p>
                  <p className={classes.title}>
                    {' '}
                    {dataName === 'ou' ? (
                      <>
                        Departments Synced
                        <br />
                        This Week
                      </>
                    ) : (
                      <>
                        Groups Synced
                        <br />
                        This Week
                      </>
                    )}
                  </p>
                </Paper>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <Paper className={classes.gridPaper}>
                  <p className={classes.value}>
                    {dataName === 'ou'
                      ? appStatistics?.departments_synced_this_month ?? 0
                      : appStatistics?.groups_synced_this_month ?? 0}
                  </p>
                  <p className={classes.title}>
                    {' '}
                    {dataName === 'ou' ? (
                      <>
                        Departments Synced
                        <br />
                        This Month
                      </>
                    ) : (
                      <>
                        Groups Synced
                        <br />
                        This Month
                      </>
                    )}
                  </p>
                </Paper>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <Paper className={classes.gridPaper}>
                  <p className={classes.value}>
                    {dataName === 'ou'
                      ? appStatistics?.departments_deleted_this_week ?? 0
                      : appStatistics?.groups_deleted_this_week ?? 0}
                  </p>
                  <p className={classes.title}>
                    {' '}
                    {dataName === 'ou' ? (
                      <>
                        Departments Deleted
                        <br />
                        This Week
                      </>
                    ) : (
                      <>
                        Groups Deleted
                        <br />
                        This Week
                      </>
                    )}
                  </p>
                </Paper>
              </Grid>

              <Grid item xs={6} sm={4} md={2}>
                <Paper className={classes.gridPaper}>
                  <p className={classes.value}>
                    {dataName === 'ou'
                      ? appStatistics?.departments_deleted_this_month ?? 0
                      : appStatistics?.groups_deleted_this_month ?? 0}
                  </p>
                  <p className={classes.title}>
                    {' '}
                    {dataName === 'ou' ? (
                      <>
                        Departments Deleted
                        <br />
                        This Month
                      </>
                    ) : (
                      <>
                        Groups Deleted
                        <br />
                        This Month
                      </>
                    )}
                  </p>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </>
      )}
      <div
        style={{
          width: '100%',
          padding: isMobile ? '4px' : isTablet ? '8px' : '16px',
          overflowX: 'auto',
        }}
      >
        {(user === 'Manager' || user === 'EndUser') && (
          <h1
            style={{
              fontWeight: '500',
              fontSize: isMobile ? '16px' : isTablet ? '18px' : '20px',
              textAlign: 'center',
              margin: isMobile ? '4px' : isTablet ? '6px' : '10px',
            }}
          >
            {dataLabel}
          </h1>
        )}
        <Paper
          className={classes.dataGrid}
          elevation={0}
          style={{
            overflowX: 'auto',
            width: '100%',
          }}
        >
          <div
            style={{
              minWidth: isMobile ? 900 : '100%',
              overflowX: 'auto',
              display: getDataReq.isFetching || loading ? 'none' : 'block',
            }}
          >
            <DataGrid
              className={classes.noBorder}
              autoHeight
              rows={formattedRows}
              columns={columns}
              pageSize={pageSize}
              rowsPerPageOptions={rowsPerPageOptions}
              pagination
              onPageChange={(params) => setPageSize(params.pageSize)}
              // onCellClick={(event: GridCellParams) =>
              //   clickedData(event.row.originalId || event.row.dn)
              // }
              onCellClick={(params: GridCellParams) => {
                // if (params.field !== 'name') return;

                const id = params.row.dn;
                if (id) {
                  clickedData(id);
                }
              }}
              density={isMobile ? 'compact' : 'standard'}
              disableColumnSelector={isMobile}
              disableColumnMenu={isMobile}
              disableSelectionOnClick
            />
          </div>
        </Paper>
        <ConfirmActionDialog
          label={deleteLabel}
          appnotify={appStatus}
          appnotifyStatus={setAppStatus}
          buttonLabel='Delete'
          openModal={openConfirmDialog}
          reSyncProvider={selectedItem?.reSyncProvider || []}
          gitStatus={selectedItem?.gitStatus || ''}
          action={handleDeleteAction}
          handleClose={() => {
            setOpenConfirmDialog(false);
            setAppStatus(false);
          }}
        />
        <DepartmentAction
          label={deleteLabel}
          appnotify={appStatus}
          appnotifyStatus={setAppStatus}
          status={dataValue}
          buttonLabel='Delete'
          openModal={openDepartmentDialog}
          reSyncProvider={selectedItem?.reSyncProvider || []}
          action={handleDeleteAction}
          handleClose={() => setOpenDepartmentDialog(false)}
        />
      </div>
    </>
  );
};

export default CommonTable;
